import { isDefined } from "@omni/utils/helper.utils";
import { createContext, Dispatch, ReactNode, SetStateAction, use } from "react";

import { MAX_CATEGORIES } from "@/components/General/FlyoutMenu/flyoutMenu.config";
import { ActiveCategoryIndexes } from "@/components/General/FlyoutMenu/types/activeCategoryIndexes.type";
import { FlyoutCategory } from "@/redux/modules/value/flyoutCategories/flyoutCategories.types";

type FlyoutMenuContextValue = {
    categories: Array<FlyoutCategory>;
    activeCategoryIndexes: ActiveCategoryIndexes;
    setActiveCatetoryIndexes: Dispatch<SetStateAction<ActiveCategoryIndexes>>;
    onClose: () => void;
};

type FlyoutMenuContextProviderProps = {
    rootCategory: FlyoutCategory;
    activeCategoryIndexes: ActiveCategoryIndexes;
    setActiveCatetoryIndexes: Dispatch<SetStateAction<ActiveCategoryIndexes>>;
    onClose: () => void;
    children: ReactNode;
};

export const FlyoutMenuContext = createContext<
    FlyoutMenuContextValue | undefined
>(undefined);
FlyoutMenuContext.displayName = "FlyoutMenuContext";

export const FlyoutMenuContextProvider = ({
    rootCategory,
    activeCategoryIndexes,
    setActiveCatetoryIndexes,
    onClose,
    children
}: FlyoutMenuContextProviderProps) => {
    const categories = activeCategoryIndexes
        .filter(isDefined)
        .slice(0, MAX_CATEGORIES - 1)
        .reduce(
            (acc, curr) => {
                const subCategory = acc.at(-1)?.subCategories?.[curr];
                if (!subCategory) return acc;

                acc.push(subCategory);

                return acc;
            },
            [rootCategory]
        );

    return (
        <FlyoutMenuContext
            value={{
                categories: categories,
                activeCategoryIndexes: activeCategoryIndexes,
                setActiveCatetoryIndexes: setActiveCatetoryIndexes,
                onClose: onClose
            }}
        >
            {children}
        </FlyoutMenuContext>
    );
};

export const useFlyoutMenuContext = () => {
    const context = use<FlyoutMenuContextValue | undefined>(FlyoutMenuContext);

    if (!isDefined(context)) {
        throw new Error(`${FlyoutMenuContext.displayName} is undefined.`);
    }

    return context;
};

export const useCategory = (index: number) => {
    const { categories } = useFlyoutMenuContext();

    return { category: categories[index] };
};
